import React from 'react'
import { addDays, min, startOfMonth } from 'date-fns'

type BlockCalendarProps = {
  days: Date[]
  selectedDay: Date | undefined
  onSelectDay: (day: Date) => void
  minimize?: boolean
  className?: string
}

const calendarItemClass = 'flex h-[35px] w-[35px] items-center justify-center'

export const BlockCalendar: React.FC<BlockCalendarProps> = React.memo(
  ({ days, selectedDay, onSelectDay, minimize = true, className }) => {
    const sortedDays = days.sort((a, b) => a.getTime() - b.getTime())
    const startDay = sortedDays[0]
    const endDay = sortedDays[sortedDays.length - 1]

    const firstSunday = new Date(minimize ? startDay : startOfMonth(startDay))
    firstSunday.setDate(firstSunday.getDate() - firstSunday.getDay())
    const lastSaturday = new Date(endDay)
    lastSaturday.setDate(lastSaturday.getDate() + (6 - lastSaturday.getDay()))

    // make sure we have at least 4 weeks between firstDunday and lastSaturday, if not minimized
    while (!minimize && lastSaturday.getTime() - firstSunday.getTime() < 3 * 7 * 24 * 60 * 60 * 1000)
      lastSaturday.setDate(lastSaturday.getDate() + 7)

    const weeks = []
    let currentDay = new Date(firstSunday)
    while (currentDay <= lastSaturday) {
      const week = []
      for (let i = 0; i < 7; i++) {
        week.push(new Date(currentDay))
        currentDay = addDays(currentDay, 1)
      }
      weeks.push(week)
    }

    return (
      <div className={`flex flex-col gap-1 rounded-lg bg-logo p-4 text-white -sm:py-8 ${className}`}>
        <div className="mx-auto text-xl font-bold sm:text-lg">
          {getDisplayedMonthYear(firstSunday, lastSaturday, startDay, endDay, minimize)}
        </div>
        <div className="flex justify-between gap-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className={`${calendarItemClass} font-bold`}>
              {day}
            </div>
          ))}
        </div>
        {weeks.map((week, i) => (
          <div key={i} className="flex justify-between gap-2">
            {week.map((day) => (
              <CalendarDayItem
                key={day.getTime()}
                day={day}
                selectedDay={selectedDay}
                sortedDays={sortedDays}
                onSelectDay={onSelectDay}
              />
            ))}
          </div>
        ))}
      </div>
    )
  }
)

const getDisplayedMonthYear = (
  firstSunday: Date,
  lastSaturday: Date,
  startDay: Date,
  endDay: Date,
  minimize?: boolean
) => {
  const startMonth = minimize
    ? firstSunday.toLocaleString('default', { month: 'long' })
    : startDay.toLocaleString('default', { month: 'long' })
  const startYear = minimize ? firstSunday.getFullYear() : startDay.getFullYear()
  const endMonth = minimize
    ? lastSaturday.toLocaleString('default', { month: 'long' })
    : endDay.toLocaleString('default', { month: 'long' })
  const endYear = minimize ? lastSaturday.getFullYear() : endDay.getFullYear()
  if (startYear === endYear) {
    if (startMonth === endMonth) {
      return `${startMonth} ${startYear}`
    } else {
      return `${startMonth}-${endMonth} ${startYear}`
    }
  } else {
    return `${startMonth} ${startYear}-${endMonth} ${endYear}`
  }
}

const CalendarDayItem: React.FC<{
  day: Date
  selectedDay: Date | undefined
  sortedDays: Date[]
  onSelectDay: (day: Date) => void
}> = ({ day, selectedDay, sortedDays, onSelectDay }) => {
  const isSelectable = sortedDays.find((d) => d.toDateString() === day.toDateString())
  const isSelected = selectedDay && day.toDateString() === selectedDay.toDateString()
  const isToday = day.toDateString() === new Date().toDateString()
  return (
    <div
      key={day.getTime()}
      className={`${calendarItemClass} rounded-full ${day.getDate() === 1 ? 'font-bold' : ''} ${
        isSelectable ? 'cursor-pointer outline-2 outline-black/5 hover:outline' : 'opacity-50'
      } ${isSelected ? 'bg-white/25' : ''} ${isToday ? 'border-2 border-white' : ''}`}
      onClick={isSelectable ? () => onSelectDay(day) : undefined}
    >
      {day.getDate()}
    </div>
  )
}
